import React from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { 
  AcademicCapIcon,
  ClockIcon,
  ChartBarIcon,
  SparklesIcon,
  ArrowLeftIcon,
  ArrowPathIcon,
  BookOpenIcon,
  LightBulbIcon,
  FireIcon,
  ArrowTrendingUpIcon
} from '@heroicons/react/24/outline';

const Flashcards: React.FC = () => {
  const pageUrl = 'https://upmindy.com/docs/flashcards';
  const pageTitle = 'Flashcards com Repetição Espaçada | Upmindy';
  const pageDescription = 'Aprenda a usar os flashcards do Upmindy com repetição espaçada inteligente para memorização eficiente e retenção de longo prazo.';

  return (
    <div className="prose dark:prose-invert max-w-none">
      <Helmet>
        <title>{pageTitle}</title>
        <meta name="title" content={pageTitle} />
        <meta name="description" content={pageDescription} />
        <link rel="canonical" href={pageUrl} />
        
        <meta property="og:type" content="article" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={pageTitle} />
        <meta property="og:description" content={pageDescription} />
        
        <meta name="twitter:card" content="summary_large_image" />
        <meta name="twitter:title" content={pageTitle} />
        <meta name="twitter:description" content={pageDescription} />
      </Helmet>

      <Link 
        to="/docs" 
        className="inline-flex items-center text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 mb-6 no-underline"
      >
        <ArrowLeftIcon className="h-4 w-4 mr-2" />
        Voltar para Documentação
      </Link>

      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 dark:text-white mb-4">
          Flashcards com Repetição Espaçada
        </h1>
        <p className="text-xl text-gray-600 dark:text-gray-300 leading-relaxed">
          Os flashcards do Upmindy usam um algoritmo de repetição espaçada cientificamente comprovado 
          para otimizar sua memorização e garantir retenção de longo prazo.
        </p>
      </div>

      <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-gray-900 dark:to-gray-800 p-8 rounded-2xl border border-blue-100 dark:border-gray-700 shadow-sm mb-8 not-prose">
        <div className="flex items-center mb-4">
          <div className="p-3 bg-gradient-to-br from-blue-500 to-blue-600 rounded-xl shadow-lg">
            <ClockIcon className="h-6 w-6 text-white" />
          </div>
          <h2 className="ml-4 text-xl font-bold text-gray-900 dark:text-gray-100">
            O que é Repetição Espaçada?
          </h2>
        </div>
        <p className="text-gray-600 dark:text-gray-400 leading-relaxed">
          É uma técnica de aprendizagem que apresenta informações em intervalos crescentes ao longo do tempo. 
          Você revisa cada cartão logo antes de esquecê-lo, fortalecendo a memória de forma eficiente e natural.
        </p>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Como Funciona</h2>
      
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Ciclo de Estudo</h3>
        <ol className="space-y-4 text-gray-600 dark:text-gray-300">
          <li className="flex items-start">
            <span className="flex-shrink-0 w-8 h-8 bg-blue-500 dark:bg-blue-400 text-white rounded-full flex items-center justify-center font-bold mr-3">1</span>
            <div>
              <strong className="text-gray-900 dark:text-white">Ver a pergunta:</strong> O cartão mostra o lado frontal (pergunta, palavra, conceito).
            </div>
          </li>
          <li className="flex items-start">
            <span className="flex-shrink-0 w-8 h-8 bg-blue-500 dark:bg-blue-400 text-white rounded-full flex items-center justify-center font-bold mr-3">2</span>
            <div>
              <strong className="text-gray-900 dark:text-white">Pensar na resposta:</strong> Tente lembrar a resposta antes de revelar.
            </div>
          </li>
          <li className="flex items-start">
            <span className="flex-shrink-0 w-8 h-8 bg-blue-500 dark:bg-blue-400 text-white rounded-full flex items-center justify-center font-bold mr-3">3</span>
            <div>
              <strong className="text-gray-900 dark:text-white">Revelar e avaliar:</strong> Veja a resposta e avalie sua dificuldade.
            </div>
          </li>
          <li className="flex items-start">
            <span className="flex-shrink-0 w-8 h-8 bg-blue-500 dark:bg-blue-400 text-white rounded-full flex items-center justify-center font-bold mr-3">4</span>
            <div>
              <strong className="text-gray-900 dark:text-white">Sistema ajusta:</strong> Baseado na sua avaliação, o próximo intervalo é calculado automaticamente.
            </div>
          </li>
        </ol>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Níveis de Dificuldade</h2>
      
      <p className="text-gray-600 dark:text-gray-300 mb-6">
        Ao revisar um cartão, você escolhe uma das quatro opções para indicar o quão bem você lembrou:
      </p>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-8 not-prose">
        <div className="bg-red-50 dark:bg-red-900/20 p-5 rounded-xl border border-red-200 dark:border-red-800">
          <h3 className="font-bold text-red-700 dark:text-red-300 mb-2">Novamente</h3>
          <p className="text-sm text-red-600 dark:text-red-400 mb-2">Não lembrei ou errei completamente</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">O cartão será mostrado novamente em breve</p>
        </div>
        
        <div className="bg-orange-50 dark:bg-orange-900/20 p-5 rounded-xl border border-orange-200 dark:border-orange-800">
          <h3 className="font-bold text-orange-700 dark:text-orange-300 mb-2">Difícil</h3>
          <p className="text-sm text-orange-600 dark:text-orange-400 mb-2">Lembrei, mas com muito esforço</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Intervalo menor que o normal</p>
        </div>
        
        <div className="bg-blue-50 dark:bg-blue-900/20 p-5 rounded-xl border border-blue-200 dark:border-blue-800">
          <h3 className="font-bold text-blue-700 dark:text-blue-300 mb-2">Bom</h3>
          <p className="text-sm text-blue-600 dark:text-blue-400 mb-2">Lembrei bem, com algum esforço</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Intervalo padrão otimizado</p>
        </div>
        
        <div className="bg-green-50 dark:bg-green-900/20 p-5 rounded-xl border border-green-200 dark:border-green-800">
          <h3 className="font-bold text-green-700 dark:text-green-300 mb-2">Fácil</h3>
          <p className="text-sm text-green-600 dark:text-green-400 mb-2">Lembrei instantaneamente, muito fácil</p>
          <p className="text-xs text-gray-600 dark:text-gray-400">Intervalo máximo aumentado</p>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Criando Decks</h2>
      
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Três formas de criar</h3>
        
        <div className="space-y-4">
          <div className="flex items-start">
            <div className="flex-shrink-0 p-2 bg-blue-100 dark:bg-blue-900/30 rounded-lg mr-3">
              <AcademicCapIcon className="h-5 w-5 text-blue-600 dark:text-blue-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Manual</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Crie cartões do zero, adicionando frente e verso manualmente. Ideal para conteúdo personalizado.
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex-shrink-0 p-2 bg-purple-100 dark:bg-purple-900/30 rounded-lg mr-3">
              <SparklesIcon className="h-5 w-5 text-purple-600 dark:text-purple-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Com IA</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Gere cartões automaticamente usando inteligência artificial. Forneça um tema e deixe a IA criar o conteúdo.
              </p>
            </div>
          </div>
          
          <div className="flex items-start">
            <div className="flex-shrink-0 p-2 bg-green-100 dark:bg-green-900/30 rounded-lg mr-3">
              <ChartBarIcon className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <div>
              <h4 className="font-semibold text-gray-900 dark:text-white mb-1">Importar Arquivo</h4>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Envie um arquivo (PDF, TXT, CSV) e a IA extrairá automaticamente os conceitos principais para criar cartões.
              </p>
            </div>
          </div>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Modos de Estudo</h2>
      
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-8">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-4">Escolha como estudar</h3>
        
        <div className="space-y-6">
          <div className="border-l-4 border-blue-500 pl-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
              <ArrowPathIcon className="h-6 w-6 mr-2 text-blue-600 dark:text-blue-400" />
              Com Repetição Espaçada (Recomendado)
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              O sistema calcula automaticamente quando você deve revisar cada cartão baseado no seu desempenho. 
              Cartões que você acha difícil aparecem com mais frequência, enquanto cartões fáceis aparecem em 
              intervalos cada vez maiores.
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
              <li className="flex items-start">
                <span className="text-green-500 mr-2 mt-0.5">✓</span>
                <span><strong>Otimiza a memorização:</strong> Baseado em pesquisas científicas sobre como o cérebro aprende</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2 mt-0.5">✓</span>
                <span><strong>Economiza tempo:</strong> Você revisa apenas quando precisa, evitando revisões desnecessárias</span>
              </li>
              <li className="flex items-start">
                <span className="text-green-500 mr-2 mt-0.5">✓</span>
                <span><strong>Retenção de longo prazo:</strong> Ideal para conhecimento que você quer lembrar permanentemente</span>
              </li>
            </ul>
          </div>

          <div className="border-l-4 border-purple-500 pl-4">
            <h4 className="font-semibold text-gray-900 dark:text-white mb-2 flex items-center">
              <BookOpenIcon className="h-6 w-6 mr-2 text-purple-600 dark:text-purple-400" />
              Sem Repetição Espaçada
            </h4>
            <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
              Você pode desativar a repetição espaçada nas configurações do deck e estudar os cartões em ordem 
              sequencial ou aleatória, quantas vezes quiser, sem o sistema agendar revisões automáticas.
            </p>
            <ul className="text-sm text-gray-600 dark:text-gray-400 space-y-2">
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-0.5">•</span>
                <span><strong>Estudo livre:</strong> Revisite qualquer cartão quando quiser, sem restrições</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-0.5">•</span>
                <span><strong>Ideal para:</strong> Revisão antes de provas, prática intensiva ou exploração de conteúdo</span>
              </li>
              <li className="flex items-start">
                <span className="text-blue-500 mr-2 mt-0.5">•</span>
                <span><strong>Controle total:</strong> Você decide quando e quantas vezes quer estudar cada cartão</span>
              </li>
            </ul>
          </div>
        </div>

        <div className="mt-6 p-4 bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-lg border border-amber-200 dark:border-amber-800">
          <p className="text-sm text-gray-700 dark:text-gray-300 flex items-start">
            <LightBulbIcon className="h-5 w-5 mr-2 flex-shrink-0 text-amber-600 dark:text-amber-400" />
            <span>
              <strong>Dica:</strong> Você pode alternar entre os modos a qualquer momento nas configurações do deck. 
              Muitos usuários usam repetição espaçada para aprendizado de longo prazo e desativam para revisões 
              intensivas antes de provas.
            </span>
          </p>
        </div>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Melhores Práticas</h2>
      
      <div className="bg-white dark:bg-gray-800 p-6 rounded-xl border border-gray-200 dark:border-gray-700 shadow-sm mb-8">
        <ul className="space-y-3 text-gray-600 dark:text-gray-300">
          <li className="flex items-start">
            <span className="text-green-500 mr-2">✓</span>
            <span><strong>Estude diariamente:</strong> Consistência é mais importante que duração. 15 minutos por dia é melhor que 2 horas uma vez por semana.</span>
          </li>
          <li className="flex items-start">
            <span className="text-green-500 mr-2">✓</span>
            <span><strong>Seja honesto nas avaliações:</strong> Avaliar corretamente sua lembrança ajuda o algoritmo a otimizar seu aprendizado.</span>
          </li>
          <li className="flex items-start">
            <span className="text-green-500 mr-2">✓</span>
            <span><strong>Cartões simples:</strong> Um conceito por cartão. Cartões complexos são difíceis de memorizar.</span>
          </li>
          <li className="flex items-start">
            <span className="text-green-500 mr-2">✓</span>
            <span><strong>Use contexto:</strong> Adicione exemplos ou contexto quando necessário para evitar ambiguidade.</span>
          </li>
          <li className="flex items-start">
            <span className="text-green-500 mr-2">✓</span>
            <span><strong>Revise os vencidos:</strong> Priorize cartões com revisão atrasada antes de adicionar novos.</span>
          </li>
        </ul>
      </div>

      <h2 className="text-2xl font-semibold text-gray-900 dark:text-white mb-6">Estatísticas e Progresso</h2>
      
      <p className="text-gray-600 dark:text-gray-300 mb-6">
        Acompanhe seu progresso através de estatísticas detalhadas:
      </p>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8 not-prose">
        <div className="bg-gradient-to-br from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 p-5 rounded-xl border border-blue-100 dark:border-blue-800">
          <ChartBarIcon className="h-8 w-8 mb-2 text-blue-600 dark:text-blue-400" />
          <h3 className="font-bold text-gray-900 dark:text-white text-sm mb-1">Taxa de Acerto</h3>
          <p className="text-xs text-gray-600 dark:text-gray-400">Porcentagem de respostas corretas ao longo do tempo</p>
        </div>
        
        <div className="bg-gradient-to-br from-green-50 to-emerald-50 dark:from-green-900/20 dark:to-emerald-900/20 p-5 rounded-xl border border-green-100 dark:border-green-800">
          <FireIcon className="h-8 w-8 mb-2 text-orange-600 dark:text-orange-400" />
          <h3 className="font-bold text-gray-900 dark:text-white text-sm mb-1">Sequência de Estudos</h3>
          <p className="text-xs text-gray-600 dark:text-gray-400">Dias consecutivos estudando</p>
        </div>
        
        <div className="bg-gradient-to-br from-purple-50 to-pink-50 dark:from-purple-900/20 dark:to-pink-900/20 p-5 rounded-xl border border-purple-100 dark:border-purple-800">
          <ArrowTrendingUpIcon className="h-8 w-8 mb-2 text-purple-600 dark:text-purple-400" />
          <h3 className="font-bold text-gray-900 dark:text-white text-sm mb-1">Cartões Maduros</h3>
          <p className="text-xs text-gray-600 dark:text-gray-400">Cartões com intervalos longos (21+ dias)</p>
        </div>
      </div>

      <div className="bg-gradient-to-br from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 rounded-2xl p-6 border border-amber-200 dark:border-amber-800 not-prose">
        <div className="flex items-start">
          <LightBulbIcon className="h-8 w-8 mr-4 flex-shrink-0 text-amber-600 dark:text-amber-400" />
          <div>
            <h3 className="text-lg font-bold text-gray-900 dark:text-white mb-2">
              Dica para Iniciantes
            </h3>
            <p className="text-gray-700 dark:text-gray-300 leading-relaxed">
              Comece com 10-15 cartões novos por dia. Conforme você se acostuma com o sistema e sua 
              carga de revisões, você pode aumentar gradualmente. Lembre-se: cada cartão novo gerará 
              várias revisões futuras, então qualidade é melhor que quantidade!
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Flashcards;
